import { useEffect, useMemo, useState } from 'react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { api } from '../../lib/api';
import { avecContexteNav } from '../../lib/mini-app-contexte';
import { enDecrochage, estExtrapolee } from './fluence-reperes';

const RETOUR_ECOLE = { contexte: "Vue de l'école", retourHref: '/mini-apps/fluence-direction', retourLabel: "Vue de l'école" };
import { JOURS_RECENT, lignesParNiveau, lignesParClasse, type LigneNiveau, type LigneClasse } from './fluence-progression-ecole';

interface Point {
  evaluationId: string; studentId: string; prenom: string; nom: string; eleveActif: boolean;
  completedAt: string; classroomId: string; classroomNom: string; levelCode: string;
  identiteEleveId: string; classroomActuelId: string | null; classroomActuelNom: string | null;
  supportTitre: string; dureeSecondes: number;
  mclm: number; seuilMin: number | null; seuilMax: number | null;
  position: 'sous' | 'dans' | 'au-dessus' | null;
  comprehensionReussies: number | null; comprehensionTotal: number | null;
}

export default function FluenceDirection() {
  const [points, setPoints] = useState<Point[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    void api.direction.mySchoolId.query()
      .then(async (schoolId) => {
        /* `mySchoolId` peut rendre `null` — un administrateur global n'a pas de
           rôle de direction porté sur une école. On partait quand même
           interroger l'API avec `null`, la validation refusait, et l'écran
           annonçait « les mesures n'ont pas pu être lues » : un échec de lecture
           là où il n'y a simplement pas d'école à lire. Le message nommait une
           cause fausse, et pour l'exact public à qui la carte a été ouverte. */
        if (!schoolId) {
          setErreur("Aucune école ne vous est rattachée : cette vue s'adresse à la direction d'un établissement.");
          setPoints([]);
          return;
        }
        const points = await (api as never as { miniApps: { fluence: { progression: { ecole: { query: (i: unknown) => Promise<Point[]> } } } } })
          .miniApps.fluence.progression.ecole.query({ schoolId });
        setPoints(points);
      })
      .catch(() => setErreur("Les mesures de l'école n'ont pas pu être lues."));
  }, []);

  const lignes = useMemo<readonly LigneNiveau[]>(() => lignesParNiveau(points ?? []), [points]);
  const lignesClasse = useMemo<readonly LigneClasse[]>(() => lignesParClasse(points ?? []), [points]);

  /* La DERNIERE lecture de chaque eleve, pas toutes : un eleve qui progresse ne
     doit pas rester dans la liste a cause d'une mesure ancienne. L'API rend le
     plus recent d'abord, donc la premiere rencontree est la bonne. */
  /* LES ÉLÈVES QUI DÉCROCHENT, distincts de ceux dont la dernière lecture est
     faible. Une lecture faible arrive à tout le monde ; deux d'affilée sont une
     tendance. Le directeur voyait la photo, il voit maintenant le mouvement. */
  const decrochage = useMemo(() => {
    const parEleve = new Map<string, Point[]>();
    for (const p of points ?? []) {
      const l = parEleve.get(p.identiteEleveId);
      if (l) l.push(p); else parEleve.set(p.identiteEleveId, [p]);
    }
    return [...parEleve.values()]
      .filter((lectures) => lectures[0]!.classroomActuelId !== null && enDecrochage(lectures))
      .map((lectures) => lectures[0]!)
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
  }, [points]);

  const sousLeRepere = useMemo(() => {
    const vu = new Set<string>();
    const dernieres: Point[] = [];
    for (const p of points ?? []) {
      if (vu.has(p.identiteEleveId)) continue;
      vu.add(p.identiteEleveId);
      dernieres.push(p);
    }
    return dernieres
      /* Un élève sans inscription active n'a plus à figurer dans une liste
         d'élèves à aider : son historique reste lisible, mais le directeur
         n'agira pas sur quelqu'un qui n'est plus là. */
      .filter((p) => p.classroomActuelId !== null && p.position === 'sous')
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr'));
  }, [points]);

  const total = (points ?? []).length;
  const carte = 'bg-card border border-border rounded-card shadow-card p-5';

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Fluence — vue de l&apos;école</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Où en est la lecture, classe par classe, et où la mesure manque.
          </p>
        </div>
      </HeaderContent>

      <main className="max-w-[1000px] mx-auto px-5 pb-16 pt-4 flex flex-col gap-5">
        {erreur ? (
          <p className="m-0 rounded-sm bg-naia-orange-bg px-4 py-3 text-sm text-foreground">{erreur}</p>
        ) : null}

        {points === null ? null : total === 0 ? (
          <section className={carte}>
            <p className="m-0 text-[16px] font-bold text-foreground mb-1">Aucune lecture mesurée pour l&apos;instant.</p>
            <p className="m-0 text-sm text-muted-foreground">
              Les mesures apparaissent ici dès qu&apos;un enseignant fait passer une lecture depuis
              « Fluence — suivi de classe ».
            </p>
          </section>
        ) : (
          <>
            <section className={carte}>
              <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Par classe</h2>
              {/* « Sous le repère » n'est PAS une alerte en soi : au CE1 de
                  septembre, c'est l'attendu. Ce qui se lit ici est un ÉCART,
                  pas un verdict — et il se regarde à côté du nombre de mesures. */}
              <p className="m-0 mb-4 text-[13px] text-muted-foreground">
                Un effectif sous le repère n&apos;est pas une alerte en soi : en début d&apos;année, c&apos;est
                l&apos;attendu. Ce qui compte est le mouvement, et le fait qu&apos;on mesure.
                Les colonnes d&apos;élèves comptent les <b>élèves actuellement inscrits dans la classe</b>,
                d&apos;après leur dernière lecture, où qu&apos;elle ait été prise. Les deux dernières
                comptent des <b>lectures</b> prises dans la classe au moment de la mesure : une
                classe récente peut avoir des élèves mesurés et aucune lecture à son nom.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[12px] uppercase tracking-[0.04em] text-muted-foreground">
                      <th className="py-2 pr-3 font-bold">Classe</th>
                      <th className="py-2 pr-3 font-bold">Élèves mesurés</th>
                      <th className="py-2 pr-3 font-bold">Élèves sous</th>
                      <th className="py-2 pr-3 font-bold">Élèves dans</th>
                      <th className="py-2 pr-3 font-bold">Élèves au-delà</th>
                      <th className="py-2 pr-3 font-bold">Lectures</th>
                      <th className="py-2 font-bold">Depuis {JOURS_RECENT} j</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignesClasse.map((l) => (
                      <tr key={l.classroomId} className="border-t border-border">
                        <td className="py-2.5 pr-3 font-extrabold text-foreground">
                          {l.classroomNom}
                          {l.lecturesHeritees > 0 ? (
                            <span className="block text-[12px] font-normal text-muted-foreground">
                              dont {l.lecturesHeritees} lecture{l.lecturesHeritees > 1 ? 's' : ''} prise{l.lecturesHeritees > 1 ? 's' : ''} dans une autre classe
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2.5 pr-3 text-foreground tabular-nums">{l.eleves}</td>
                        <td className="py-2.5 pr-3 tabular-nums text-naia-orange font-bold">{l.sous}</td>
                        <td className="py-2.5 pr-3 tabular-nums text-naia-green-deep font-bold">{l.dans}</td>
                        <td className="py-2.5 pr-3 tabular-nums text-naia-blue font-bold">{l.au}</td>
                        <td className="py-2.5 pr-3 text-foreground tabular-nums">{l.mesures}</td>
                        <td className="py-2.5 tabular-nums text-muted-foreground">{l.recentes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Le CP n'a pas de repère publié : ses lignes « sous / dans /
                  au-delà » resteront vides, et c'est normal. */}
              <p className="m-0 mt-3 text-[13px] text-muted-foreground">
                Au CP, aucun repère chiffré n&apos;est publié : ses colonnes restent vides.
              </p>
            </section>

            {decrochage.length > 0 ? (
              <section className={carte}>
                <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">À suivre de près</h2>
                <p className="m-0 mb-3 text-[13px] text-muted-foreground">
                  Élèves dont les <b>deux dernières lectures</b> sont sous le repère de leur niveau.
                  Une lecture faible arrive à tout le monde ; deux d&apos;affilée sont une tendance.
                </p>
                <ul className="list-none m-0 p-0 flex flex-col gap-2">
                  {decrochage.map((p) => (
                    <li key={p.studentId}>
                      <a
                        href={avecContexteNav(`/mini-apps/fluence-eleve-detail?student=${p.studentId}`, RETOUR_ECOLE)}
                        className="min-h-[44px] flex items-center gap-3 rounded-sm border border-naia-orange bg-naia-orange-bg px-4 py-3 no-underline"
                      >
                        <span className="font-bold text-sm text-foreground truncate">{p.prenom} {p.nom}</span>
                        <span className="text-[13px] text-muted-foreground">{p.classroomActuelNom}</span>
                        <span className="ml-auto shrink-0 text-[13px] font-extrabold text-naia-orange">
                          {p.mclm} MCLM{estExtrapolee(p.dureeSecondes) ? ' · extrapolé' : ''}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className={carte}>
              <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Ouvrir un élève</h2>
              {/* La vue d'école sert à repérer, pas à classer : ces élèves sont
                  ceux dont la DERNIÈRE lecture est sous le repère, dans l'ordre
                  alphabétique — pas du plus faible au plus fort. */}
              <p className="m-0 mb-3 text-[13px] text-muted-foreground">
                Élèves dont la dernière lecture est sous le repère de leur niveau, par ordre
                alphabétique. Ouvrir un nom montre sa courbe.
              </p>
              <ul className="list-none m-0 p-0 flex flex-col gap-2">
                {sousLeRepere.length === 0 ? (
                  <li className="text-sm text-muted-foreground">Aucun élève sous le repère de son niveau.</li>
                ) : sousLeRepere.map((p) => (
                  <li key={p.studentId}>
                    <a
                      href={avecContexteNav(`/mini-apps/fluence-eleve-detail?student=${p.studentId}`, RETOUR_ECOLE)}
                      className="min-h-[44px] flex items-center gap-3 rounded-sm border border-border bg-card px-4 py-3 no-underline"
                    >
                      <span className="font-bold text-sm text-foreground truncate">{p.prenom} {p.nom}</span>
                      <span className="text-[13px] text-muted-foreground">{p.classroomActuelNom}</span>
                      <span className="ml-auto shrink-0 text-[13px] font-extrabold text-foreground">
                        {p.mclm} MCLM{estExtrapolee(p.dureeSecondes) ? ' · extrapolé' : ''}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            <section className={carte}>
              <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Là où la mesure manque</h2>
              {/* Ce que la direction demande vraiment : anticiper. Or un élève
                  jamais mesuré est plus inquiétant qu'un élève sous le repère —
                  le second est vu, le premier est invisible. Cette section reste
                  PAR NIVEAU, à la différence du tableau « Par classe » ci-dessus :
                  une classe qui n'a jamais fait passer de lecture n'a aucune ligne
                  dans ce tableau-là (rien à grouper), alors que les cinq niveaux
                  sont connus d'avance et peuvent donc être annoncés vides. */}
              <p className="m-0 mb-3 text-[13px] text-muted-foreground">
                Un niveau qu&apos;on ne mesure pas est plus difficile à aider qu&apos;un niveau en
                difficulté : le second se voit, le premier ne se voit pas.
              </p>
              <ul className="list-none m-0 p-0 flex flex-col gap-2">
                {lignes.filter((l) => l.recentes === 0).map((l) => (
                  <li key={l.niveau} className="flex items-center gap-3 rounded-sm border border-border bg-card px-4 py-3">
                    <span className="font-extrabold text-sm text-foreground">{l.niveau}</span>
                    <span className="text-[13px] text-muted-foreground">
                      {l.mesures === 0
                        ? 'jamais mesuré'
                        : `aucune mesure depuis ${JOURS_RECENT} jours — ${l.mesures} au total`}
                    </span>
                  </li>
                ))}
                {lignes.every((l) => l.recentes > 0) ? (
                  <li className="text-sm text-muted-foreground">Tous les niveaux ont été mesurés ce mois-ci.</li>
                ) : null}
              </ul>
            </section>
          </>
        )}
      </main>
    </>
  );
}
